import type { ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import {
  type TransactionsScope,
  type TransactionStore,
} from '@/features/billing/transactions/TransactionStore'
import { InfiniteScrollProvider } from '@/infra/InfiniteScrollProvider'
import { type BillingProfileStore } from '@/features/billing/profile/BillingProfileStore'
import { type Transaction } from '@/features/billing/transactions/Transaction'
import {
  BillingMethod,
  type StripeCardMethod,
} from '@/features/billing/profile/BillingMethod'
import { computed } from 'mobx'
import { ViewModelList } from '@/infra/view-model-list'
import { roundToPrecision } from '@/utils/number-util'
import type { SnackbarRetryWrapperFactory } from '@/features/shell'
import { task } from 'mobx-task'
import type { GetCurrentMemberFn } from '@/features/iam'

/**
 * View model for the transactions table.
 */
export class TransactionsTableViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()

  /**
   * Transaction view models.
   */
  readonly transactionVms

  /**
   * Scroll provider for querying transactions.
   */
  readonly scrollProvider = new InfiniteScrollProvider({
    limit: 10,
    fetchItems: async ({ paging, signal }) => {
      const withRetry = this._createSnackbarRetryWrapper(
        'There was an issue retrieving payment history',
        signal,
      )

      return withRetry(async () =>
        this._transactionStore.listTransactions(
          {
            scope: this.scope,
            ...paging,
          },
          signal,
        ),
      )
    },
  })

  constructor(
    private readonly _createSnackbarRetryWrapper: SnackbarRetryWrapperFactory,
    private readonly _transactionStore: TransactionStore,
    private readonly _billingProfileStore: BillingProfileStore,
    private readonly _getCurrentMember: GetCurrentMemberFn,
    public readonly scope: TransactionsScope,
  ) {
    // We only care about charges and refunds.
    const isRelevantTx = (t: Transaction) =>
      t.transactionType === 'Charge' || t.transactionType === 'Refund'

    this.transactionVms = new ViewModelList({
      models: () => this.scrollProvider.items.filter(isRelevantTx),
      create: (t) => new TransactionItemViewModel(this._billingProfileStore, t),
    })
    this._lifecycle.manage(this.transactionVms)
  }

  /**
   * Whether there are not transactions.
   */
  @computed
  get isEmpty() {
    return this.transactionVms.items.length === 0
  }

  /** @inheritDoc */
  activate() {
    this._lifecycle.activate()

    void this.fetchBillingProfileForMember()

    void this.scrollProvider.fetch({
      signal: this._lifecycle.signal,
      staleWhileRevalidate: true,
    })
  }

  /** @inheritDoc */
  deactivate() {
    this._lifecycle.deactivate()
  }

  /**
   * Fetches the billing profile for the current member.
   */
  fetchBillingProfileForMember = task(async () => {
    await this._billingProfileStore.fetchBillingProfileForMember(
      this._getCurrentMember(),
    )
  })
}

/**
 * View model for the transaction item.
 */
export class TransactionItemViewModel {
  constructor(
    private readonly _billingProfileStore: BillingProfileStore,
    public readonly transaction: Transaction,
  ) {}

  @computed
  get isCard() {
    const method = this._billingMethod

    return (
      method.type.type === 'Stripe' ||
      method.type.type === 'StripeCardPaymentMethod'
    )
  }

  @computed
  get stripeCardBillingMethod() {
    const method = this._billingMethod

    return this.isCard ? (method.type as StripeCardMethod) : null
  }

  /**
   * Gets the billing method card brand, if applicable.
   */
  @computed
  get billingMethodBrand() {
    return this.stripeCardBillingMethod?.brand ?? 'unknown'
  }

  /**
   * The last 4 digits of the card.
   */
  @computed
  get billingMethodLast4() {
    return this.stripeCardBillingMethod?.last4 ?? 'unknown'
  }

  /**
   * The display name of the transaction
   */
  @computed
  get displayName() {
    if (this.transaction.jobName) {
      return this.transaction.jobName
    }

    return this.isRefund ? 'Refund' : 'Charge'
  }

  /**
   * The transaction amount, normalized from units.
   */
  @computed
  get amount() {
    return roundToPrecision(this.transaction.amount / 100)
  }

  /**
   * The transaction amount, normalized from units, with a minus sign
   * if the transaction is a refund.
   */
  @computed
  get signedAmount() {
    return this.isRefund ? -this.amount : this.amount
  }

  /**
   * Whether this is a refund transaction.
   */
  @computed
  get isRefund() {
    return this.transaction.transactionType === 'Refund'
  }

  /**
   * The billing method that the transaction was made with.
   * @private
   */
  @computed
  private get _billingMethod() {
    for (const profile of this._billingProfileStore.profiles.items) {
      const found = profile.billingMethods.find(
        (b) => b.id === this.transaction.billingMethodId,
      )
      if (found) {
        return found
      }
    }

    return BillingMethod.none
  }
}
